import React, { useState } from 'react';
import { FaAngleDown } from 'react-icons/fa';

const FAQ_HEADING_RE = /^<h([23])[^>]*>([\s\S]*?)<\/h\1>$/i;
const stripTags = (html) => html.replace(/<[^>]+>/g, '').trim();

/**
 * Blog `body` arrays store every section — including FAQs — as flat HTML
 * strings with no structural marker. This scans for a "Frequently asked
 * questions" heading and groups the question/answer blocks that follow it
 * so they can be rendered as an accordion instead of static HTML.
 *
 * Returns the original body untouched (heading: null, items: []) whenever
 * no FAQ section is found or the content after the heading doesn't match
 * the expected question/answer shape, so nothing is ever silently dropped.
 */
export function splitFaqFromBody(body) {
    if (!Array.isArray(body)) {
        return { before: body || [], heading: null, items: [], after: [] };
    }

    const faqIndex = body.findIndex((item) => {
        const match = typeof item === 'string' && item.match(FAQ_HEADING_RE);
        return !!match && stripTags(match[2]).toLowerCase() === 'frequently asked questions';
    });

    if (faqIndex === -1) {
        return { before: body, heading: null, items: [], after: [] };
    }

    const headingMatch = body[faqIndex].match(FAQ_HEADING_RE);
    const heading = stripTags(headingMatch[2]);

    const items = [];
    let i = faqIndex + 1;
    while (i < body.length) {
        const questionMatch = typeof body[i] === 'string' && body[i].match(FAQ_HEADING_RE);
        if (!questionMatch || !stripTags(questionMatch[2]).endsWith('?')) {
            break;
        }

        const question = questionMatch[2];
        i += 1;

        const answerParts = [];
        while (i < body.length && typeof body[i] === 'string' && /^<p[\s>]/i.test(body[i])) {
            answerParts.push(body[i]);
            i += 1;
        }

        if (answerParts.length === 0) {
            break;
        }

        items.push({ question, answer: answerParts.join('') });
    }

    if (items.length === 0) {
        return { before: body, heading: null, items: [], after: [] };
    }

    return {
        before: body.slice(0, faqIndex),
        heading,
        items,
        after: body.slice(i),
    };
}

const BlogFaqAccordion = ({ heading, items }) => {
    const [openIndex, setOpenIndex] = useState(null);

    if (!items || items.length === 0) {
        return null;
    }

    const toggle = (index) => {
        setOpenIndex((current) => (current === index ? null : index));
    };

    return (
        <div className="section section-padding-equal bg-color-light blog-faq-section">
            <div className="container">
                {heading && <h2 className="blog-faq-heading">{heading}</h2>}
                <div className="blog-faq-grid">
                {items.map((item, index) => {
                    const isOpen = openIndex === index;
                    const buttonId = `blog-faq-question-${index}`;
                    const panelId = `blog-faq-panel-${index}`;

                    return (
                        <div className={`blog-faq-card${isOpen ? ' is-open' : ''}`} key={index}>
                            <h3 className="blog-faq-card-title">
                                <button
                                    type="button"
                                    id={buttonId}
                                    className="blog-faq-toggle"
                                    aria-expanded={isOpen}
                                    aria-controls={panelId}
                                    onClick={() => toggle(index)}
                                >
                                    <span
                                        className="blog-faq-question"
                                        dangerouslySetInnerHTML={{ __html: item.question }}
                                    />
                                    <span className="blog-faq-icon" aria-hidden="true">
                                        <FaAngleDown />
                                    </span>
                                </button>
                            </h3>
                            <div
                                id={panelId}
                                role="region"
                                aria-labelledby={buttonId}
                                aria-hidden={!isOpen}
                                className={`blog-faq-answer-wrap${isOpen ? ' is-open' : ''}`}
                            >
                                <div className="blog-faq-answer-inner">
                                    <div
                                        className="blog-faq-answer-content"
                                        dangerouslySetInnerHTML={{ __html: item.answer }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
                </div>
            </div>
        </div>
    );
};

export default BlogFaqAccordion;
